import { useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Reveal } from "@/components/landing/Reveal";
import { useBooking } from "@/context/BookingContext";
import { createLead } from "@/lib/api";

export const FooterCta = () => {
  const { openBooking } = useBooking();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await createLead({ email, source: "footer_guide" });
      setDone(true);
      toast.success("Thank you! Our team has received your request.");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative overflow-hidden bg-ink px-7 py-28 grain sm:px-12 lg:py-36" data-testid="footer-cta-section">
      <div className="relative z-10 mx-auto max-w-3xl text-center">
        <Reveal>
          <h2 className="font-display text-4xl leading-tight text-white sm:text-5xl lg:text-6xl">
            Every holiday should leave you <span className="italic text-gold">refreshed.</span>
            <br />Not exhausted.
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-white/55 sm:text-lg">
            Let our local teams manage the complexities of mountain terrain while your
            family enjoys the hills.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          {done ? (
            <div className="mx-auto mt-12 flex max-w-md items-center justify-center gap-3 border border-gold/40 bg-white/5 p-6" data-testid="lead-success">
              <CheckCircle2 className="h-6 w-6 text-gold" />
              <p className="text-white/80">Your guide is on its way. Our team will reach out shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mx-auto mt-12 flex max-w-md flex-col gap-3 sm:flex-row" data-testid="lead-form">
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Corporate Email Address"
                data-testid="lead-email-input"
                className="h-14 rounded-none border-white/20 bg-white/5 px-5 text-white placeholder:text-white/40 focus-visible:ring-gold"
              />
              <Button
                type="submit"
                disabled={loading}
                data-testid="lead-submit-btn"
                className="group h-14 shrink-0 rounded-none bg-gold px-6 text-sm font-bold uppercase tracking-[0.1em] text-ink hover:bg-gold-hover"
              >
                {loading ? "Sending…" : "Claim Guide"}
                <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </form>
          )}
          <p className="mx-auto mt-5 max-w-lg text-xs leading-relaxed text-white/40">
            Instant Download: <span className="italic text-white/60">{"\u201CThe Burnout Cure: A Professional\u2019s Guide to Northeast Logistics\u201D"}</span> sent straight to your inbox.
          </p>

          <button
            data-testid="footer-consult-btn"
            onClick={() => openBooking()}
            className="mt-8 text-sm font-medium text-gold underline-offset-4 transition-colors hover:text-gold-hover hover:underline"
          >
            Or schedule a private 1-to-1 consultation →
          </button>
        </Reveal>
      </div>

      <footer className="relative z-10 mx-auto mt-24 max-w-6xl border-t border-white/10 pt-10 text-center">
        <img src="/master-logo.png" alt="NORTHEASTGO" className="mx-auto h-14 w-auto" />
        <p className="mt-4 text-xs text-white/40">Premium South India → Northeast Corridor · © {new Date().getFullYear()}</p>
      </footer>
    </section>
  );
};
